import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { FaGlobe, FaCog } from 'react-icons/fa';

const LanguageSelectorHeader: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedLanguage, setSelectedLanguage, availableLanguages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleLanguageSelect = (languageTag: string) => {
    const language = availableLanguages.find((lang) => lang.iso639_tag === languageTag);
    if (!language) {
      return;
    }

    setSelectedLanguage(language);
    setIsOpen(false);
    // Reload to ensure language-dependent content refreshes across the app
    window.location.reload();
  };

  return (
    <div className="fixed top-0 left-0 right-0 bg-base-100/95 border-b border-base-300 backdrop-blur-sm z-40 safe-area-top">
      <div className="flex items-center justify-between px-4 py-2 max-w-screen-xl mx-auto">
        <div className="relative" ref={dropdownRef}>
          <button
            type='button'
            className="btn btn-ghost btn-circle text-2xl"
            onClick={() => setIsOpen((prev) => !prev)}
            aria-label={t('languageSelector.changeLanguage')}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            {selectedLanguage?.flag || <FaGlobe />}
          </button>

          {isOpen && (
            <div className="absolute left-0 mt-2 w-48 rounded-box border border-base-300 bg-base-200 shadow-lg z-50">
              <ul className="menu menu-sm" role="listbox">
                {availableLanguages.map((language) => (
                  <li key={language.iso639_tag}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selectedLanguage?.iso639_tag === language.iso639_tag}
                      onClick={() => handleLanguageSelect(language.iso639_tag)}
                      className={`flex w-full items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                        selectedLanguage?.iso639_tag === language.iso639_tag
                          ? 'bg-base-100 font-semibold'
                          : 'hover:bg-base-100/80'
                      }`}
                    >
                      <span className="text-lg leading-none">{language.flag}</span>
                      <span className="flex-1 text-left">{language.language_name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="hidden sm:block">
          <h1 className="text-lg font-bold">GPTandem</h1>
        </div>

        <button
          type="button"
          className="btn btn-ghost btn-circle"
          onClick={() => navigate('/settings')}
          aria-label={t('navigation.settings')}
          title={t('navigation.settings')}
        >
          <FaCog size={20} />
        </button>
      </div>
    </div>
  );
};

export default LanguageSelectorHeader;
